import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", data: null });
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", data: null }); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", data });
          toast.success("Payment confirmed");
          return;
        }
        if (["failed", "expired", "refunded"].includes(data.payment_status)) {
          setState({ status: "failed", data });
          return;
        }
      } catch (e) {
        setState({ status: "error", data: null });
        return;
      }
      tries.current += 1;
      if (tries.current > 20) { setState({ status: "timeout", data: null }); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const nextHref = state.data?.metadata?.job_id
    ? `/jobs/${state.data.metadata.job_id}`
    : "/dashboard";

  return (
    <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-4">
      <div className="w-full max-w-md border border-neutral-800 bg-neutral-900 p-8 text-center" data-testid="payment-success-page">
        {state.status === "polling" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto text-amber-500 animate-spin" />
            <div className="display text-2xl mt-4">Confirming payment…</div>
            <p className="text-neutral-400 text-sm mt-2">Hold tight, this only takes a few seconds.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-amber-500" strokeWidth={2} />
            <div className="display text-3xl mt-4">Payment received</div>
            <p className="text-neutral-400 text-sm mt-2">
              {state.data?.lookup_key === "job_boost_7d"
                ? "Your job is now boosted for 7 days — it will appear at the top of the board with an URGENT ribbon."
                : "Thank you — your purchase is confirmed."}
            </p>
            <Link
              to={nextHref}
              data-testid="payment-success-continue"
              className="mt-6 inline-block px-6 py-3 bg-amber-500 text-neutral-950 font-bold uppercase tracking-widest text-xs hover:bg-amber-400 transition-colors"
            >
              Continue
            </Link>
          </>
        )}
        {(state.status === "failed" || state.status === "timeout" || state.status === "error") && (
          <>
            <XCircle className="w-12 h-12 mx-auto text-red-400" />
            <div className="display text-2xl mt-4">Something went wrong</div>
            <p className="text-neutral-400 text-sm mt-2">Your payment couldn&apos;t be confirmed. If you were charged, contact support.</p>
            <Link to="/dashboard" className="mt-6 inline-block px-6 py-3 border border-neutral-800 hover:border-amber-500 hover:text-amber-500 font-bold uppercase tracking-widest text-xs">
              Back to dashboard
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
